import {
  ETIQUETA_FUENTE_DE_PRECIO,
  ETIQUETA_FUENTE_DE_PRECIO_EN,
  INCOTERMS_DE_DOCUMENTOS,
  type DocumentoGenerado,
  type EstadoDeDocumentos,
  type IncotermDeDocumentos,
  type PaqueteDeDocumentos,
  type ResultadoDeGeneracion,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {
  etiquetaDePaqueteCorta,
  rutaDeDocumentoGenerado,
  rutaDelZipDelLote,
  useEstadoDeDocumentos,
  useGenerarDocumentos,
} from '../api/documentos';
import {et, tr} from '../i18n/idioma';
import {fecha, fechaHora, mes, numero} from '../utilidades/formato';
import {AYUDA_DOCUMENTOS as DOC} from './ayuda-exportacion';
import {DocumentosBluequest} from './documentos-bluequest';
import {NotaDeAyuda} from './nota-de-ayuda';
import {ProvisionesDeExportacion} from './provisiones-de-exportacion';
import {usePermisos} from './sesion-contexto';

const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';
const campo = 'mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta';

type Dialogo = 'facturacion' | 'aduana' | null;

// Los documentos de la exportacion se generan en el servidor con los mismos
// generadores de siempre y quedan archivados con sus datos de entrada.
export function PaqueteDocumental({codigo}: {codigo: string}) {
  const permisos = usePermisos();
  const estado = useEstadoDeDocumentos(codigo);
  const generar = useGenerarDocumentos(codigo);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [resultado, setResultado] = useState<ResultadoDeGeneracion | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const ejecutar = async (paquete: 'departure' | 'control-de-empaque' | 'certificados-de-origen') => {
    setMensaje(null);
    try {
      setResultado(await generar.mutateAsync({paquete}));
    } catch (error) {
      setMensaje(
        error instanceof ErrorDeApi
          ? [error.message, ...(error.detalles ?? [])].join(' ')
          : tr('No se pudo generar.', 'Could not generate.'),
      );
    }
  };

  return (
    <div className="rounded-lg border border-linea bg-white p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-medium">{tr('Paquete documental', 'Document package')}</h2>
        {estado.data && (
          <span className="text-xs text-apagado">
            {numero(estado.data.bigBags)} Big Bags · {tr('precios UPME:', 'UPME prices:')}{' '}
            {et(ETIQUETA_FUENTE_DE_PRECIO, ETIQUETA_FUENTE_DE_PRECIO_EN)[estado.data.precios.fuente]}
            {estado.data.precios.ultimoMes ? ` (${tr('hasta', 'through')} ${mes(estado.data.precios.ultimoMes)})` : ''}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-apagado">
        {tr(
          'Cada documento se genera en el servidor y queda guardado con la fecha, quién lo pidió y los datos usados.',
          'Each document is generated on the server and saved with the date, who requested it and the data used.',
        )}
      </p>
      {estado.isError && <p className="mt-3 text-sm text-alerta">{estado.error.message}</p>}
      {estado.data && (
        <>
          {permisos.editar ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className={boton}
                disabled={generar.isPending}
                onClick={() => void ejecutar('control-de-empaque')}
              >
                ↓ {tr('Control de empaque', 'Packing control')}
              </button>
              <button
                type="button"
                className={boton}
                disabled={generar.isPending}
                onClick={() => void ejecutar('departure')}
              >
                ↓ Departure + Packing List
              </button>
              <button
                type="button"
                className={boton}
                disabled={generar.isPending}
                onClick={() => void ejecutar('certificados-de-origen')}
                title={tr(
                  'Una fila por recolección con lo de la exportación; lo del certificado queda para llenarlo.',
                  'One row per pickup with the export data; the certificate data is left to fill in.',
                )}
              >
                ↓ {tr('Tabla relación certificados', 'Certificates relation table')}
              </button>
              {estado.data.conDocumentosDeAduana && (
                <>
                  <button
                    type="button"
                    className={botonSuave}
                    disabled={generar.isPending}
                    onClick={() => setDialogo('facturacion')}
                  >
                    ↓ {tr('Instrucciones de facturación', 'Invoicing instructions')}
                  </button>
                  <button
                    type="button"
                    className={botonSuave}
                    disabled={generar.isPending}
                    onClick={() => setDialogo('aduana')}
                  >
                    ↓ {tr('Documentos de aduana', 'Customs documents')}
                  </button>
                </>
              )}
              {generar.isPending && (
                <span className="self-center text-sm text-apagado">{tr('Generando…', 'Generating…')}</span>
              )}
            </div>
          ) : (
            <p className="mt-3 text-sm text-apagado">
              {tr(
                'Tu rol es de consulta: puedes descargar los documentos ya generados, pero no generar nuevos.',
                'Your role is Viewer: you can download documents already generated, but not generate new ones.',
              )}
            </p>
          )}
          <Avisos estado={estado.data} />
          {mensaje && <p className="mt-3 text-sm text-alerta">{mensaje}</p>}
          {estado.data.bluequest && permisos.editar && (
            <DocumentosBluequest codigo={codigo} estado={estado.data.bluequest} onResultado={setResultado} />
          )}

          {dialogo === 'facturacion' && (
            <DialogoDeFacturacion
              estado={estado.data}
              pendiente={generar.isPending}
              onCancelar={() => setDialogo(null)}
              onGenerar={async entradas => {
                setMensaje(null);
                try {
                  setResultado(await generar.mutateAsync({paquete: 'instrucciones-de-facturacion', entradas}));
                  setDialogo(null);
                } catch (error) {
                  setMensaje(
                    error instanceof ErrorDeApi
                      ? [error.message, ...(error.detalles ?? [])].join(' ')
                      : tr('No se pudo generar.', 'Could not generate.'),
                  );
                }
              }}
            />
          )}
          {dialogo === 'aduana' && (
            <DialogoDeAduana
              estado={estado.data}
              pendiente={generar.isPending}
              onCancelar={() => setDialogo(null)}
              onGenerar={async entradas => {
                setMensaje(null);
                try {
                  setResultado(await generar.mutateAsync({paquete: 'documentos-de-aduana', entradas}));
                  setDialogo(null);
                } catch (error) {
                  setMensaje(
                    error instanceof ErrorDeApi
                      ? [error.message, ...(error.detalles ?? [])].join(' ')
                      : tr('No se pudo generar.', 'Could not generate.'),
                  );
                }
              }}
            />
          )}

          {resultado && <Resultado codigo={codigo} resultado={resultado} onCerrar={() => setResultado(null)} />}
          {estado.data.conDocumentosDeAduana && <ProvisionesDeExportacion codigo={codigo} onResultado={setResultado} />}
          <Historial codigo={codigo} generados={estado.data.generados} />
        </>
      )}
    </div>
  );
}

function Avisos({estado}: {estado: EstadoDeDocumentos}) {
  const avisos: string[] = [];
  if (estado.bigBags === 0)
    avisos.push(
      tr(
        'Esta exportación no tiene Big Bags en el inventario: no hay nada que poner en los documentos.',
        'This export has no Big Bags in the inventory: there is nothing to put in the documents.',
      ),
    );
  if (estado.conDocumentosDeAduana) {
    if (!estado.ajustesConfigurados)
      avisos.push(
        tr(
          'Faltan los datos de firmante, vendedor y consignatario para los documentos de aduana.',
          'The signer, seller and consignee details for the customs documents are missing.',
        ),
      );
    if (!estado.plantillas.letter)
      avisos.push(
        tr(
          'Falta la plantilla Word de la carta aclaratoria para los documentos de aduana.',
          'The Word template of the clarification letter for the customs documents is missing.',
        ),
      );
    if (!estado.plantillas.letterhead || !estado.plantillas.signature)
      avisos.push(
        tr(
          'Sin membrete o firma registrados, la carta DIAN y la lista de empaque salen sin ellos.',
          'Without a registered letterhead or signature, the DIAN letter and the packing list come out without them.',
        ),
      );
  }
  if (estado.sinMesDeMolienda > 0)
    avisos.push(
      tr(
        `${numero(estado.sinMesDeMolienda)} Big Bags no tienen mes de molienda: su regalía sale en cero.`,
        `${numero(estado.sinMesDeMolienda)} Big Bags have no milling month: their royalty comes out as zero.`,
      ),
    );
  if (estado.precios.aviso) avisos.push(estado.precios.aviso);
  if (!avisos.length) return null;
  return (
    <ul className="mt-3 space-y-1 rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-xs">
      {avisos.map(a => (
        <li key={a}>⚠ {a}</li>
      ))}
    </ul>
  );
}

function Resultado({
  codigo,
  resultado,
  onCerrar,
}: {
  codigo: string;
  resultado: ResultadoDeGeneracion;
  onCerrar: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-verde/40 bg-verde-claro p-4 text-sm" role="status">
      <div className="flex items-start justify-between gap-3">
        <div className="font-medium text-verde-oscuro">
          {etiquetaDePaqueteCorta(resultado.paquete)}: {numero(resultado.documentos.length)}{' '}
          {resultado.documentos.length === 1
            ? tr('archivo generado', 'file generated')
            : tr('archivos generados', 'files generated')}
        </div>
        <button type="button" onClick={onCerrar} className="text-xs text-apagado hover:text-tinta">
          ✕ {tr('Cerrar', 'Close')}
        </button>
      </div>
      <ul className="mt-2 space-y-1">
        {resultado.documentos.map(d => (
          <li key={d.id}>
            <a href={rutaDeDocumentoGenerado(d.id)} className="text-verde hover:underline" download>
              ↓ {d.nombreArchivo}
            </a>
            <span className="ml-2 text-xs text-apagado">{tamano(d.tamanoBytes)}</span>
          </li>
        ))}
      </ul>
      {resultado.documentos.length > 1 && (
        <a
          href={rutaDelZipDelLote(codigo, resultado.lote)}
          className="mt-2 inline-block font-medium text-verde hover:underline"
          download
        >
          ↓ {tr('Descargar todo en un ZIP', 'Download everything as a ZIP')}
        </a>
      )}
      {resultado.avisos.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs">
          {resultado.avisos.map(a => (
            <li key={a}>⚠ {a}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Historial({codigo, generados}: {codigo: string; generados: DocumentoGenerado[]}) {
  const [abierto, setAbierto] = useState(false);
  if (!generados.length)
    return (
      <p className="mt-4 text-xs text-apagado">
        {tr(
          'Todavía no se ha generado ningún documento para esta exportación.',
          'No documents have been generated for this export yet.',
        )}
      </p>
    );
  // Agrupados por generacion (lote), de la mas reciente a la mas antigua.
  const lotes = new Map<string, DocumentoGenerado[]>();
  for (const g of generados) lotes.set(g.lote, [...(lotes.get(g.lote) ?? []), g]);
  const lista = [...lotes.entries()];
  const visibles = abierto ? lista : lista.slice(0, 3);
  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium">
          {tr('Documentos generados', 'Generated documents')} ({numero(generados.length)})
        </h3>
        {lista.length > 3 && (
          <button type="button" onClick={() => setAbierto(a => !a)} className="text-xs text-verde hover:underline">
            {abierto
              ? tr('Ver menos', 'Show less')
              : tr(
                  `Ver todos (${numero(lista.length)} generaciones)`,
                  `Show all (${numero(lista.length)} generations)`,
                )}
          </button>
        )}
      </div>
      <ul className="mt-2 space-y-2 text-sm">
        {visibles.map(([lote, docs]) => {
          const primero = docs[0]!;
          return (
            <li key={lote} className="rounded-md border border-linea px-3 py-2">
              <div className="flex flex-wrap justify-between gap-2 text-xs text-apagado">
                <span>
                  {etiquetaDePaqueteCorta(primero.paquete)} · {fechaHora(primero.generadoEl)}
                  {primero.generadoPor ? ` · ${primero.generadoPor}` : ''}
                </span>
                <span>
                  {primero.fuenteDePrecios
                    ? primero.fuenteDePrecios === 'live'
                      ? tr('Precios UPME: en línea', 'UPME prices: online')
                      : tr('Precios UPME: de respaldo', 'UPME prices: fallback')
                    : ''}
                  {docs.length > 1 && (
                    <a href={rutaDelZipDelLote(codigo, lote)} className="ml-3 text-verde hover:underline" download>
                      ↓ ZIP
                    </a>
                  )}
                </span>
              </div>
              <ul className="mt-1 grid gap-x-6 gap-y-0.5 sm:grid-cols-2">
                {docs.map(d => (
                  <li key={d.id} className="truncate">
                    <a
                      href={rutaDeDocumentoGenerado(d.id)}
                      className="text-verde hover:underline"
                      download
                      title={d.nombreArchivo}
                    >
                      ↓ {d.nombreArchivo}
                    </a>
                  </li>
                ))}
              </ul>
              <ResumenDeEntradas entradas={primero.entradas} paquete={primero.paquete} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Los datos de entrada que conviene ver de un vistazo.
function ResumenDeEntradas({entradas, paquete}: {entradas: Record<string, unknown>; paquete: PaqueteDeDocumentos}) {
  const partes: string[] = [];
  const n = (v: unknown, decimales = 2) => (typeof v === 'number' ? numero(v, decimales) : null);
  if (paquete === 'instrucciones-de-facturacion' || paquete === 'documentos-de-aduana' || paquete === 'provisiones') {
    if (n(entradas.trm)) partes.push(`TRM ${n(entradas.trm)}`);
    if (n(entradas.precioOro)) partes.push(`${tr('oro', 'gold')} ${n(entradas.precioOro)}`);
    if (n(entradas.precioPlata)) partes.push(`${tr('plata', 'silver')} ${n(entradas.precioPlata, 3)}`);
    if (typeof entradas.incoterm === 'string') partes.push(entradas.incoterm);
  }
  if (paquete === 'documentos-de-aduana') {
    if (typeof entradas.fechaFactura === 'string')
      partes.push(`${tr('factura', 'invoice')} ${fecha(entradas.fechaFactura)}`);
    const facturas = entradas.facturasPorMina;
    if (facturas && typeof facturas === 'object')
      partes.push(
        Object.entries(facturas as Record<string, string>)
          .map(([mina, numeroDeFactura]) => `${mina}: ${numeroDeFactura}`)
          .join(', '),
      );
  }
  if (paquete === 'bluequest') {
    if (typeof entradas.referencia === 'string') partes.push(entradas.referencia);
    if (n(entradas.precioAu)) partes.push(`${tr('oro', 'gold')} ${n(entradas.precioAu)}`);
    if (n(entradas.precioAg)) partes.push(`${tr('plata', 'silver')} ${n(entradas.precioAg, 3)}`);
    if (typeof entradas.banco === 'string') partes.push(entradas.banco);
    if (n(entradas.cargosUsd)) partes.push(`TC USD ${n(entradas.cargosUsd)}`);
  }
  if (!partes.length) return null;
  return <div className="mt-1 text-xs text-apagado">{partes.join(' · ')}</div>;
}

// ── Dialogos ────────────────────────────────────────────────────────────────

type EntradasComunes = {trm: string; precioOro: string; precioPlata: string; incoterm: IncotermDeDocumentos};

function CamposComunes({valores, onCambiar}: {valores: EntradasComunes; onCambiar: (v: EntradasComunes) => void}) {
  return (
    <div className="grid gap-3 sm:grid-cols-4">
      <label className="block text-xs text-apagado">
        {tr('TRM (pesos por dólar)', 'TRM (pesos per dollar)')}
        <NotaDeAyuda titulo={tr('TRM (pesos por dólar)', 'TRM (pesos per dollar)')} texto={DOC.trm} dentroDeEtiqueta />
        <input
          type="number"
          step="0.01"
          min="0"
          required
          value={valores.trm}
          onChange={e => onCambiar({...valores, trm: e.target.value})}
          className={campo}
        />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Precio del oro (USD/oz)', 'Gold price (USD/oz)')}
        <NotaDeAyuda
          titulo={tr('Precio del oro (USD/oz)', 'Gold price (USD/oz)')}
          texto={DOC.precioOro}
          dentroDeEtiqueta
        />
        <input
          type="number"
          step="0.01"
          min="0"
          required
          value={valores.precioOro}
          onChange={e => onCambiar({...valores, precioOro: e.target.value})}
          className={campo}
        />
      </label>
      <label className="block text-xs text-apagado">
        {tr('Precio de la plata (USD/oz)', 'Silver price (USD/oz)')}
        <NotaDeAyuda
          titulo={tr('Precio de la plata (USD/oz)', 'Silver price (USD/oz)')}
          texto={DOC.precioPlata}
          dentroDeEtiqueta
        />
        <input
          type="number"
          step="0.001"
          min="0"
          required
          value={valores.precioPlata}
          onChange={e => onCambiar({...valores, precioPlata: e.target.value})}
          className={campo}
        />
      </label>
      <label className="block text-xs text-apagado">
        Incoterm
        <NotaDeAyuda titulo="Incoterm" texto={DOC.incoterm} dentroDeEtiqueta />
        <select
          value={valores.incoterm}
          onChange={e => onCambiar({...valores, incoterm: e.target.value as IncotermDeDocumentos})}
          className={campo}
        >
          {INCOTERMS_DE_DOCUMENTOS.map(i => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

const numeroDe = (texto: string) => Number(String(texto).replace(',', '.'));

// TRM, precios e incoterm de la ultima instruccion de facturacion (o de los
// ultimos documentos de aduana), para no volver a escribirlos.
const entradasIniciales = (estado: EstadoDeDocumentos): EntradasComunes => {
  const s = estado.valoresSugeridos;
  return s
    ? {
        trm: String(s.trm),
        precioOro: String(s.precioOro),
        precioPlata: String(s.precioPlata),
        incoterm: (INCOTERMS_DE_DOCUMENTOS as readonly string[]).includes(s.incoterm)
          ? (s.incoterm as IncotermDeDocumentos)
          : 'CIF Mundra-India',
      }
    : {trm: '', precioOro: '', precioPlata: '', incoterm: 'CIF Mundra-India'};
};

// De donde salieron los valores, y el precio pagable por onza como referencia.
function OrigenDeLosValores({estado}: {estado: EstadoDeDocumentos}) {
  const s = estado.valoresSugeridos;
  if (!s) return null;
  return (
    <p className="text-xs text-apagado" data-prueba="valores-sugeridos">
      {s.desde === 'instrucciones-de-facturacion'
        ? tr(
            `TRM, precios e incoterm tomados de la instrucción de facturación del ${fechaHora(s.generadoEl)}.`,
            `TRM, prices and incoterm taken from the invoicing instruction of ${fechaHora(s.generadoEl)}.`,
          )
        : tr(
            `TRM, precios e incoterm tomados de los documentos de aduana del ${fechaHora(s.generadoEl)}.`,
            `TRM, prices and incoterm taken from the customs documents of ${fechaHora(s.generadoEl)}.`,
          )}{' '}
      {tr(
        `Precio pagable por onza: oro ${numero(s.precioPagableOro, 2)} USD (83 %), plata ${numero(s.precioPagablePlata, 4)} USD (71 %). Se pueden cambiar.`,
        `Payable price per ounce: gold ${numero(s.precioPagableOro, 2)} USD (83 %), silver ${numero(s.precioPagablePlata, 4)} USD (71 %). They can be changed.`,
      )}
    </p>
  );
}

function DialogoDeFacturacion({
  estado,
  pendiente,
  onCancelar,
  onGenerar,
}: {
  estado: EstadoDeDocumentos;
  pendiente: boolean;
  onCancelar: () => void;
  onGenerar: (entradas: {
    trm: number;
    precioOro: number;
    precioPlata: number;
    incoterm: IncotermDeDocumentos;
  }) => Promise<void>;
}) {
  const [valores, setValores] = useState<EntradasComunes>(() => entradasIniciales(estado));
  return (
    <form
      className="mt-4 space-y-3 rounded-lg border border-linea bg-papel p-4"
      aria-label={tr('Instrucciones de facturación', 'Invoicing instructions')}
      onSubmit={e => {
        e.preventDefault();
        void onGenerar({
          trm: numeroDe(valores.trm),
          precioOro: numeroDe(valores.precioOro),
          precioPlata: numeroDe(valores.precioPlata),
          incoterm: valores.incoterm,
        });
      }}
    >
      <div className="text-sm font-medium">{tr('Instrucciones de facturación', 'Invoicing instructions')}</div>
      <p className="text-xs text-apagado">
        {tr('Sale un archivo por mina:', 'One file per mine:')}{' '}
        {estado.minas.map(m => `${m.nombre} (${numero(m.bigBags)} BB)`).join(', ')}.
      </p>
      <CamposComunes valores={valores} onCambiar={setValores} />
      {valores.incoterm.startsWith('EXW') && (
        <p className="text-xs text-apagado" data-prueba="aviso-exw">
          {tr(
            'EXW: con la instrucción sale la provisión de cada mina (Randomizer, Niton Results y la provisión), y el total de la factura resta sus deducciones metálicas, otros cargos y penalidades.',
            'EXW: the provision of each mine comes out with the instruction (Randomizer, Niton Results and the provision), and the invoice total subtracts its metal deductions, other charges and penalties.',
          )}
        </p>
      )}
      <OrigenDeLosValores estado={estado} />
      <div className="flex gap-2">
        <button type="submit" className={boton} disabled={pendiente}>
          {pendiente ? tr('Generando…', 'Generating…') : tr('Generar', 'Generate')}
        </button>
        <button type="button" className={botonSuave} onClick={onCancelar}>
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

function DialogoDeAduana({
  estado,
  pendiente,
  onCancelar,
  onGenerar,
}: {
  estado: EstadoDeDocumentos;
  pendiente: boolean;
  onCancelar: () => void;
  onGenerar: (entradas: {
    trm: number;
    precioOro: number;
    precioPlata: number;
    incoterm: IncotermDeDocumentos;
    fletePorContenedor: number;
    bolsasPorContenedor: number;
    seguroPorMil: number;
    fechaFactura: string;
    fechaCarta: string;
    facturasPorMina: Record<string, string>;
  }) => Promise<void>;
}) {
  const [valores, setValores] = useState<EntradasComunes>(() => entradasIniciales(estado));
  const [flete, setFlete] = useState(String(estado.valoresPorDefecto.fletePorContenedor));
  const [bolsas, setBolsas] = useState(String(estado.valoresPorDefecto.bolsasPorContenedor));
  const [seguro, setSeguro] = useState(String(estado.valoresPorDefecto.seguroPorMil));
  // La fecha de la factura guardada (si ya hay); si no, hoy.
  const [fechaFactura, setFechaFactura] = useState(
    () => estado.minas.find(m => m.fechaFactura)?.fechaFactura ?? estado.hoy,
  );
  const [fechaCarta, setFechaCarta] = useState(estado.hoy);
  const [facturas, setFacturas] = useState<Record<string, string>>(() =>
    Object.fromEntries(estado.minas.map(m => [m.nombre, m.factura ?? ''])),
  );
  // Si llegan facturas nuevas, solo se llenan las casillas vacias (no se pisa lo escrito).
  useEffect(() => {
    setFacturas(actual => Object.fromEntries(estado.minas.map(m => [m.nombre, actual[m.nombre] || m.factura || ''])));
  }, [estado.minas]);
  return (
    <form
      className="mt-4 space-y-3 rounded-lg border border-linea bg-papel p-4"
      aria-label={tr('Documentos de aduana', 'Customs documents')}
      onSubmit={e => {
        e.preventDefault();
        void onGenerar({
          trm: numeroDe(valores.trm),
          precioOro: numeroDe(valores.precioOro),
          precioPlata: numeroDe(valores.precioPlata),
          incoterm: valores.incoterm,
          fletePorContenedor: numeroDe(flete),
          bolsasPorContenedor: Math.round(numeroDe(bolsas)),
          seguroPorMil: numeroDe(seguro),
          fechaFactura,
          fechaCarta,
          facturasPorMina: facturas,
        });
      }}
    >
      <div className="text-sm font-medium">{tr('Documentos de aduana', 'Customs documents')}</div>
      <p className="text-xs text-apagado">
        {tr(
          'Carta aclaratoria de transporte y seguro, carta DIAN de regalías y lista de empaque en PDF con membrete y firma, y el cálculo CIF en Excel. Los números de factura quedan guardados en la exportación.',
          'Clarification letter on freight and insurance, DIAN royalties letter and packing list as PDF with letterhead and signature, and the CIF calculation in Excel. The invoice numbers are saved in the export.',
        )}
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {estado.minas.map(m => (
          <label key={m.nombre} className="block text-xs text-apagado">
            {tr(`Factura de ${m.nombre}`, `${m.nombre} invoice`)} ({numero(m.bigBags)} BB)
            <NotaDeAyuda texto={DOC.facturaPorMina} dentroDeEtiqueta />
            <input
              required
              value={facturas[m.nombre] ?? ''}
              onChange={e => setFacturas({...facturas, [m.nombre]: e.target.value})}
              className={campo}
              aria-label={tr(`Factura de ${m.nombre}`, `${m.nombre} invoice`)}
            />
          </label>
        ))}
      </div>
      <CamposComunes valores={valores} onCambiar={setValores} />
      <OrigenDeLosValores estado={estado} />
      <div className="grid gap-3 sm:grid-cols-5">
        <label className="block text-xs text-apagado">
          {tr('Flete por contenedor (USD)', 'Freight per container (USD)')}
          <NotaDeAyuda
            titulo={tr('Flete por contenedor (USD)', 'Freight per container (USD)')}
            texto={DOC.flete}
            dentroDeEtiqueta
          />
          <input
            type="number"
            step="0.01"
            min="0"
            value={flete}
            onChange={e => setFlete(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Bolsas para dividir el flete', 'Bags to split the freight')}
          <NotaDeAyuda
            titulo={tr('Bolsas para dividir el flete', 'Bags to split the freight')}
            texto={DOC.bolsasFlete}
            dentroDeEtiqueta
          />
          <input
            type="number"
            step="1"
            min="1"
            value={bolsas}
            onChange={e => setBolsas(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Seguro (por mil)', 'Insurance (per thousand)')}
          <NotaDeAyuda
            titulo={tr('Seguro (por mil)', 'Insurance (per thousand)')}
            texto={DOC.seguro}
            dentroDeEtiqueta
          />
          <input
            type="number"
            step="0.01"
            min="0"
            value={seguro}
            onChange={e => setSeguro(e.target.value)}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Fecha de la factura', 'Invoice date')}
          <NotaDeAyuda titulo={tr('Fecha de la factura', 'Invoice date')} texto={DOC.fechaFactura} dentroDeEtiqueta />
          <input type="date" value={fechaFactura} onChange={e => setFechaFactura(e.target.value)} className={campo} />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Fecha de la carta', 'Letter date')}
          <NotaDeAyuda titulo={tr('Fecha de la carta', 'Letter date')} texto={DOC.fechaCarta} dentroDeEtiqueta />
          <input type="date" value={fechaCarta} onChange={e => setFechaCarta(e.target.value)} className={campo} />
        </label>
      </div>
      <p className="text-xs text-apagado">
        {tr(
          'El flete de 4.500 USD se divide entre 30 bolsas (150 USD por Big Bag); es una regla distinta de los 28 Big Bags por contenedor.',
          'The 4,500 USD freight is split across 30 bags (150 USD per Big Bag); this is a different rule from the 28 Big Bags per container.',
        )}
      </p>
      <div className="flex gap-2">
        <button type="submit" className={boton} disabled={pendiente}>
          {pendiente ? tr('Generando…', 'Generating…') : tr('Generar los 4 documentos', 'Generate the 4 documents')}
        </button>
        <button type="button" className={botonSuave} onClick={onCancelar}>
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

const tamano = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${numero(bytes / 1024 / 1024, 2)} MB` : `${numero(Math.max(1, Math.round(bytes / 1024)))} KB`;
